/**
 * Audit Log Service (Frontend)
 * API calls för audit log
 */

import api from '../api/client';

export interface AuditLogEntry {
  id: number;
  imei: string;
  vehicle_name?: string;
  command_code: string;
  command_name: string;
  command_type: 'GET' | 'SET' | 'ACTION' | 'CONFIG';
  parameters: any;
  priority: number;
  batch_size: number;
  batch_id?: string;
  status: 'pending' | 'sent' | 'acknowledged' | 'failed' | 'timeout' | 'expired';
  error_message?: string;
  created_at: string;
  sent_at?: string;
  acknowledged_at?: string;
  failed_at?: string;
  response_data?: any;
  response_received_at?: string;
  last_seen?: string | null; // Senaste gången enheten var uppkopplad
}

export interface AuditLogFilters {
  imei?: string;
  command_code?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface AuditLogPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface AuditLogResponse {
  success: boolean;
  data: AuditLogEntry[];
  pagination: AuditLogPagination;
}

export interface AuditLogStatistics {
  total_commands: number;
  successful: number;
  failed: number;
  pending: number;
  unique_devices: number;
  unique_command_types: number;
}

export interface CommandQueueEntry {
  batch_id: string;
  imei: string;
  status: string;
  total_commands: number;
  created_at: string;
  expires_at: string;
}

export interface CommandStatus {
  batch_id: string;
  command_id: string;
  command_code: string;
  status: 'pending' | 'sent' | 'acknowledged' | 'failed' | 'timeout';
  retries: number;
  created_at: string;
  sent_at?: string;
  acknowledged_at?: string;
  error?: string;
}

export const auditLogService = {
  /**
   * Hämta användarens egna kommandon
   */
  getMyCommands: async (
    filters?: AuditLogFilters,
    limit: number = 100,
    offset: number = 0
  ): Promise<AuditLogResponse> => {
    const params: any = { limit, offset };
    if (filters?.imei) params.imei = filters.imei;
    if (filters?.command_code) params.command_code = filters.command_code;
    if (filters?.status) params.status = filters.status;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    const response = await api.get('/api/audit-log/my-commands', { params });
    return response.data;
  },

  /**
   * Hämta kommandon för en specifik enhet
   */
  getDeviceAuditLog: async (
    imei: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<AuditLogResponse> => {
    const response = await api.get(`/api/audit-log/device/${imei}`, {
      params: { limit, offset },
    });
    return response.data;
  },

  /**
   * Hämta statistik för användaren
   */
  getMyStatistics: async (
    dateFrom?: string,
    dateTo?: string
  ): Promise<AuditLogStatistics> => {
    const params: any = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    const response = await api.get('/api/audit-log/my-statistics', { params });
    return response.data.data;
  },

  /**
   * Ta bort ett kommando (endast pending/sent)
   */
  deleteCommand: async (commandId: number): Promise<{ success: boolean; error?: string }> => {
    const response = await api.delete(`/api/audit-log/command/${commandId}`);
    return response.data;
  },

  /**
   * Hämta kommandon från kö för en specifik enhet
   */
  getDeviceCommandQueue: async (imei: string, status?: string): Promise<{
    success: boolean;
    data: {
      queues: Array<{
        batch_id: string;
        imei: string;
        status: string;
        total_commands: number;
        created_at: string;
        expires_at: string;
      }>;
      commands: Array<{
        batch_id: string;
        command_id: string;
        command_code: string;
        status: 'pending' | 'sent' | 'acknowledged' | 'failed' | 'timeout';
        retries: number;
        created_at: string;
        sent_at?: string;
        acknowledged_at?: string;
        error?: string;
      }>;
    };
  }> => {
    const params: any = {};
    if (status) params.status = status;

    const response = await api.get(`/api/devices/${imei}/commands/queue`, { params });
    return response.data;
  },
};

